import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  FaUserTie, FaSearch, FaPlus, FaTimes, FaFilePdf, FaFileUpload,
  FaInbox, FaTrash, FaCheckCircle, FaArrowRight, FaFlagCheckered, FaPen, FaEye,
  FaExclamationTriangle, FaFileExcel, FaPrint, FaSort, FaSortUp, FaSortDown, FaEnvelope,
  FaUserCheck, FaCheck, FaGraduationCap, FaBriefcase, FaCalendarAlt,
} from "react-icons/fa";
import * as api from "../api/placementApi";
import {
  T, STAGE_FILTER_OPTIONS, RESULT_OPTIONS, OUTCOME_OPTIONS, SCREENING_OUTCOME_OPTIONS, ENQUIRY_OUTCOME_OPTIONS,
  INTERVIEW_PARAMETERS, GlobalStyles, Spinner, PillSelect, StarRating, StagePill, ResultPill, StageOutcomePill,
  overlayStyle, modalStyle, primaryBtnStyle, textBtnStyle, inputStyle, labelStyle,
} from "./uiKit";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  // hour12 forced explicitly — some locales default toLocaleString to a
  // 24-hour clock, which drops the AM/PM suffix entirely.
  return d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
}

// Splits/joins a "YYYY-MM-DDTHH:mm" datetime-local value into separate date
// and time parts — the combined native datetime-local widget (calendar +
// scroll-wheel time picker crammed into one popup) is clunky and varies a
// lot across browsers, so the UI shows two plain, familiar native pickers
// side by side instead, while the underlying value stays a single string.
function splitDateTime(value) {
  if (!value) return { date: "", time: "" };
  const [date, time] = value.split("T");
  return { date: date || "", time: time || "" };
}
function joinDateTime(date, time) {
  if (!date) return "";
  // Deliberately NOT defaulting a missing time to "00:00" — that silently
  // produces a real, submittable midnight value that TimePicker then
  // faithfully displays as "12:00 AM", making an unset time look like a
  // genuine selection. Keeping it date-only until a time is actively
  // chosen (enforced via the Save-button validation below) avoids
  // scheduling something at literal midnight by accident.
  return time ? `${date}T${time}` : date;
}

// Local calendar date as "YYYY-MM-DD" — deliberately not toISOString()
// (that's UTC and can land on the wrong day near midnight in the user's
// timezone) — used as the floor for scheduling a test/interview.
function todayDateString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function daysSince(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.max(0, Math.floor(diff / 86400000));
  return days === 0 ? "Today" : `${days}d`;
}

// Latest outcome recorded on the candidate's CURRENT stage (Screening/Test/
// Interview) — used to show something more useful than a blank "—" in the
// Result column while the final result hasn't been decided yet.
function getCurrentStageOutcome(candidate) {
  const entries = (candidate.stageHistory || []).filter((h) => h.stage === candidate.currentStage && h.outcome);
  return entries.length ? entries[entries.length - 1].outcome : null;
}

// Digits-only, capped to maxLen — used to strip alphabets/symbols live as
// the user types into Mobile / Passing Year fields.
function digitsOnly(value, maxLen) {
  return value.replace(/\D/g, "").slice(0, maxLen);
}

// Single-number summary of an Interview entry's scorecard (average of
// whichever parameters have actually been rated) — used for the compact
// one-line display next to the entry in the timeline, rather than listing
// all six parameters there (that level of detail lives in the Edit/View
// modals instead).
function getAvgInterviewRating(h) {
  if (h.stage !== "INTERVIEW" || !h.evaluation) return null;
  const rated = INTERVIEW_PARAMETERS.map((p) => h.evaluation[p.key]).filter((v) => v != null);
  if (!rated.length) return null;
  return rated.reduce((sum, v) => sum + v, 0) / rated.length;
}

// Pipeline order (not alphabetical) so sorting the Stage column reads as
// Enquiry → Test → Interview → Result, not the arbitrary alphabetical order.
const STAGE_RANK = { ENQUIRY: 0, SCREENING: 1, TEST: 2, INTERVIEW: 3, RESULT: 4 };
const RESULT_RANK = { PENDING: 0, SELECTED: 1, REJECTED: 2, ON_HOLD: 3, OFFER_DECLINED: 4 };

// Table columns that support click-to-sort, in on-screen order. Column
// header cells not listed here (S.No, Resume, Actions) render as plain,
// non-clickable headers.
const SORTABLE_COLUMNS = [
  { label: "S.No" },
  { label: "Name", sortKey: "name" },
  { label: "Email", sortKey: "email" },
  { label: "Mobile", sortKey: "mobile" },
  { label: "Position Applied", sortKey: "positionApplied" },
  { label: "Education", sortKey: "qualification" },
  { label: "Location", sortKey: "location" },
  { label: "Stage", sortKey: "currentStage" },
  { label: "Result", sortKey: "result", tooltip: "Shows the final result once decided; until then, shows the outcome of the candidate's current stage (Screening/Test/Interview)." },
  { label: "Days in Stage", sortKey: "inStage", tooltip: "Time since this candidate's stage/status was last updated — a high number means they've been sitting without any action." },
  { label: "Added", sortKey: "createdAt" },
  { label: "Resume" },
  { label: "Actions" },
];

const PAGE_SIZE_OPTIONS = [
  { value: "50", label: "50" },
  { value: "100", label: "100" },
  { value: "250", label: "250" },
  { value: "500", label: "500" },
];

function getSortValue(c, key) {
  switch (key) {
    case "name": return (c.name || "").toLowerCase();
    case "email": return (c.email || "").toLowerCase();
    case "mobile": return c.mobile || "";
    case "positionApplied": return (c.positionApplied || "").toLowerCase();
    case "qualification": return (c.qualification || "").toLowerCase();
    case "location": return (c.location || "").toLowerCase();
    case "currentStage": return STAGE_RANK[c.currentStage] ?? 99;
    case "result": return RESULT_RANK[c.result] ?? 99;
    case "inStage": return new Date(c.updatedAt || c.createdAt || 0).getTime();
    case "createdAt": return new Date(c.createdAt || 0).getTime();
    default: return "";
  }
}

const errorTextStyle = { fontSize: 11.5, color: T.danger, marginTop: 4 };

// Escapes free-text fields (remarks/interviewer/round label etc. are
// admin-entered and get interpolated straight into a document.write()'d
// HTML string for printing — must be escaped to avoid injecting markup.
function escapeHtml(value) {
  if (value === null || value === undefined || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Builds a standalone, print-ready HTML document for one candidate — full
// profile + the complete stage-history timeline — opened in a new window
// and sent to the browser's print dialog (Save as PDF works there natively),
// instead of printing the on-screen app UI (buttons/icons/nav) as-is.
function buildCandidatePrintHtml(candidate) {
  const historyRows = candidate.stageHistory.length
    ? candidate.stageHistory.map((h, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${escapeHtml(h.stage)}</td>
        <td>${escapeHtml(h.roundLabel) || "—"}</td>
        <td>${h.scheduledDate ? escapeHtml(formatDateTime(h.scheduledDate)) : "—"}</td>
        <td>${escapeHtml(h.interviewer) || "—"}</td>
        <td>${escapeHtml(h.score) || "—"}</td>
        <td>${escapeHtml(h.outcome) || "—"}</td>
        <td>${escapeHtml(h.result) || "—"}</td>
        <td>${escapeHtml(h.remarks) || "—"}</td>
        <td>${h.createdAt ? escapeHtml(formatDateTime(h.createdAt)) : "—"}</td>
      </tr>`).join("")
    : `<tr><td colspan="10" style="text-align:center;color:#98a2b3;">No history yet.</td></tr>`;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(candidate.name)} - Candidate Report</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #101828; margin: 32px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #6366f1; padding-bottom: 14px; margin-bottom: 20px; }
  .header h1 { margin: 0; font-size: 22px; }
  .header .sub { font-size: 13px; color: #475467; margin-top: 4px; }
  .badges span { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; margin-left: 6px; }
  .stage-badge { background: #eef2ff; color: #4f46e5; }
  .result-badge { background: #f0fdf4; color: #16a34a; }
  .section-title { font-size: 12.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: #475467; margin: 24px 0 10px; border-bottom: 1px solid #e4e8ee; padding-bottom: 6px; }
  table.profile { width: 100%; border-collapse: collapse; }
  table.profile td { padding: 6px 8px; font-size: 13px; vertical-align: top; }
  table.profile td.label { color: #98a2b3; width: 150px; font-weight: 600; }
  table.history { width: 100%; border-collapse: collapse; margin-top: 8px; }
  table.history th, table.history td { border: 1px solid #e4e8ee; padding: 6px 8px; font-size: 11px; text-align: left; }
  table.history th { background: #f4f6f8; font-weight: 700; text-transform: uppercase; font-size: 10px; color: #475467; }
  .footer { margin-top: 30px; font-size: 10.5px; color: #98a2b3; text-align: right; }
  @page { margin: 16mm 14mm; }
</style>
</head>
<body>
  <div class="header">
    <div>
      <h1>${escapeHtml(candidate.name)}</h1>
      <div class="sub">${escapeHtml(candidate.positionApplied) || "Position not specified"}</div>
    </div>
    <div class="badges">
      <span class="stage-badge">${escapeHtml(candidate.currentStage)}</span>
      <span class="result-badge">${escapeHtml(candidate.result)}</span>
    </div>
  </div>

  <div class="section-title">Candidate Profile</div>
  <table class="profile">
    <tr><td class="label">Email</td><td>${escapeHtml(candidate.email)}</td><td class="label">Mobile</td><td>${escapeHtml(candidate.mobile)}</td></tr>
    <tr><td class="label">Qualification</td><td>${escapeHtml(candidate.qualification) || "—"}</td><td class="label">Institute</td><td>${escapeHtml(candidate.institute) || "—"}</td></tr>
    <tr><td class="label">Passing Year</td><td>${escapeHtml(candidate.passingYear) || "—"}</td><td class="label">% / CGPA</td><td>${escapeHtml(candidate.percentageOrCgpa) || "—"}</td></tr>
    <tr><td class="label">Source</td><td>${escapeHtml(candidate.source) || "—"}</td><td class="label">Location</td><td>${escapeHtml(candidate.location) || "—"}</td></tr>
    <tr><td class="label">Added On</td><td colspan="3">${escapeHtml(formatDate(candidate.createdAt))}</td></tr>
    <tr><td class="label">Resume</td><td colspan="3">${candidate.hasResume ? escapeHtml(candidate.resumeOriginalFileName) || "Uploaded" : "No resume uploaded"}</td></tr>
  </table>

  <div class="section-title">Stage History</div>
  <table class="history">
    <thead>
      <tr><th>#</th><th>Stage</th><th>Round Label</th><th>Scheduled Date &amp; Time</th><th>Tester / Interviewer</th><th>Score</th><th>Outcome</th><th>Result</th><th>Remarks</th><th>Entry Date</th></tr>
    </thead>
    <tbody>
      ${historyRows}
    </tbody>
  </table>

  <div class="footer">Generated on ${escapeHtml(new Date().toLocaleString())}</div>
</body>
</html>`;
}

/* ── Confirm modal (generic, used for delete confirmations) ─────────── */
function ConfirmModal({
  title = "Are you sure?",
  message,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  danger = true,
  loading = false,
  onConfirm,
  onCancel,
}) {
  return (
    <div style={{ ...overlayStyle, zIndex: 1100 }} onMouseDown={(e) => { if (e.target === e.currentTarget && !loading) onCancel(); }}>
      <div style={{ ...modalStyle, maxWidth: 400 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 10 }}>
          <div style={{
            width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
            background: danger ? "rgba(220,53,69,0.1)" : T.primarySoft,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FaExclamationTriangle color={danger ? T.danger : T.primary} size={16} />
          </div>
          <div style={{ paddingTop: 4 }}>
            <div style={{ fontFamily: T.fontDisplay, fontSize: 16.5, fontWeight: 700, color: T.ink1 }}>
              {title}
            </div>
            {message && (
              <div style={{ fontSize: 13.5, color: T.ink2, marginTop: 6, lineHeight: 1.5 }}>
                {message}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 22 }}>
          <button type="button" onClick={onCancel} disabled={loading} style={textBtnStyle}>
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            style={{
              ...primaryBtnStyle,
              background: danger ? T.danger : T.primary,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Deleting…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Resume viewer modal ── in-app PDF popup, instead of a bare new-tab
   open. The auth token can't be attached to a plain <iframe src>, so the
   PDF is fetched as a Blob first and embedded via an object URL. Download
   and Print are NOT duplicated here — the browser's own embedded PDF
   viewer already renders its own toolbar with both. ── */
function ResumeViewerModal({ candidateId, fileName, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    let objUrl = null;
    setLoading(true);
    setError(false);
    api.fetchResumeBlob(candidateId)
      .then((blob) => {
        if (!active) return;
        objUrl = URL.createObjectURL(blob);
        setBlobUrl(objUrl);
      })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => {
      active = false;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [candidateId]);

  return (
    <div style={{ ...overlayStyle, zIndex: 1200 }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: "min(920px, 94vw)", height: "92vh", background: T.white, borderRadius: T.radius,
        boxShadow: T.shadowMd, display: "flex", flexDirection: "column", overflow: "hidden",
        fontFamily: T.fontBody, animation: "plc-fade-in .16s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
            <FaFilePdf color={T.danger} size={15} />
            <span style={{ fontSize: 14, fontWeight: 600, color: T.ink1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {fileName || "Resume"}
            </span>
          </div>
          <button type="button" onClick={onClose} title="Close" style={iconBtnStyle}>
            <FaTimes size={15} />
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: "#525659" }}>
          {loading ? (
            <Spinner padding="160px 0" />
          ) : error ? (
            <div style={{ color: "#fff", textAlign: "center", padding: 60, fontSize: 13.5 }}>Failed to load resume.</div>
          ) : (
            <iframe src={blobUrl} title="Resume Preview" style={{ width: "100%", height: "100%", border: "none" }} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Add Candidate modal ─────────────────────────────────────────────── */
function AddCandidateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", mobile: "", qualification: "", institute: "",
    passingYear: "", percentageOrCgpa: "", positionApplied: "", source: "", location: "",
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setMobile = (e) => setForm((f) => ({ ...f, mobile: digitsOnly(e.target.value, 10) }));
  const setPassingYear = (e) => setForm((f) => ({ ...f, passingYear: digitsOnly(e.target.value, 4) }));

  const mobileValid = form.mobile.length === 10;
  const passingYearValid = form.passingYear === "" || form.passingYear.length === 4;
  const canSave = form.name.trim() && form.email.trim() && mobileValid && passingYearValid;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.create(form, resumeFile);
      toast.success("Candidate added");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to add candidate");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 560, maxHeight: "88vh", overflowY: "auto" }} className="plc-scroll">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>Add Candidate</span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} value={form.name} onChange={set("name")} placeholder="Candidate full name" />
          </div>
          <div>
            <label style={labelStyle}>Email *</label>
            <input style={inputStyle} type="email" value={form.email} onChange={set("email")} placeholder="candidate@email.com" />
          </div>
          <div>
            <label style={labelStyle}>Mobile *</label>
            <input
              style={inputStyle} value={form.mobile} onChange={setMobile}
              placeholder="9876543210" inputMode="numeric" maxLength={10}
            />
            {form.mobile.length > 0 && !mobileValid && (
              <div style={errorTextStyle}>Enter a valid 10-digit mobile number</div>
            )}
          </div>
          <MasterDropdownField master="education" label="Qualification" value={form.qualification} onChange={setField("qualification")} />
          <div>
            <label style={labelStyle}>Institute</label>
            <input style={inputStyle} value={form.institute} onChange={set("institute")} placeholder="College / University" />
          </div>
          <div>
            <label style={labelStyle}>Passing Year</label>
            <input
              style={inputStyle} value={form.passingYear} onChange={setPassingYear}
              placeholder="2024" inputMode="numeric" maxLength={4}
            />
            {!passingYearValid && (
              <div style={errorTextStyle}>Enter a valid 4-digit year</div>
            )}
          </div>
          <div>
            <label style={labelStyle}>% / CGPA</label>
            <input style={inputStyle} value={form.percentageOrCgpa} onChange={set("percentageOrCgpa")} placeholder="8.5 CGPA" />
          </div>
          <MasterDropdownField master="position" label="Position Applied" value={form.positionApplied} onChange={setField("positionApplied")} />
          <div>
            <label style={labelStyle}>Source</label>
            <input style={inputStyle} value={form.source} onChange={set("source")} placeholder="Referral / Job Portal / Walk-in" />
          </div>
          <div>
            <label style={labelStyle}>Location</label>
            <input style={inputStyle} value={form.location} onChange={set("location")} placeholder="City, State" />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Resume (PDF)</label>
            <label style={{
              display: "flex", alignItems: "center", gap: 10, border: `1px dashed ${T.borderStrong}`,
              borderRadius: 10, padding: "12px 14px", cursor: "pointer", fontSize: 13.5, color: T.ink2,
            }}>
              <FaFileUpload color={T.primary} />
              {resumeFile ? resumeFile.name : "Click to choose a PDF file"}
              <input type="file" accept="application/pdf" hidden onChange={(e) => setResumeFile(e.target.files?.[0] || null)} />
            </label>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24 }}>

          <button
            type="button"
            disabled={saving || !canSave}
            onClick={handleSave}
            style={{ ...primaryBtnStyle, opacity: saving || !canSave ? 0.55 : 1, cursor: saving || !canSave ? "not-allowed" : "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
           <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ── Edit Candidate modal ────────────────────────────────────────────── */
function EditCandidateModal({ candidate, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: candidate.name || "",
    email: candidate.email || "",
    mobile: candidate.mobile || "",
    qualification: candidate.qualification || "",
    institute: candidate.institute || "",
    passingYear: candidate.passingYear != null ? String(candidate.passingYear) : "",
    percentageOrCgpa: candidate.percentageOrCgpa || "",
    positionApplied: candidate.positionApplied || "",
    source: candidate.source || "",
    location: candidate.location || "",
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const setMobile = (e) => setForm((f) => ({ ...f, mobile: digitsOnly(e.target.value, 10) }));
  const setPassingYear = (e) => setForm((f) => ({ ...f, passingYear: digitsOnly(e.target.value, 4) }));

  const mobileValid = form.mobile.length === 10;
  const passingYearValid = form.passingYear === "" || form.passingYear.length === 4;
  const canSave = form.name.trim() && form.email.trim() && mobileValid && passingYearValid;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.update(candidate.id, form);
      if (resumeFile) {
        await api.replaceResume(candidate.id, resumeFile);
      }
      toast.success("Candidate updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to update candidate");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 560, maxHeight: "88vh", overflowY: "auto" }} className="plc-scroll">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>Edit Candidate</span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} value={form.name} onChange={set("name")} placeholder="Candidate full name" />
          </div>
          <div>
            <label style={labelStyle}>Email *</label>
            <input style={inputStyle} type="email" value={form.email} onChange={set("email")} placeholder="candidate@email.com" />
          </div>
          <div>
            <label style={labelStyle}>Mobile *</label>
            <input
              style={inputStyle} value={form.mobile} onChange={setMobile}
              placeholder="9876543210" inputMode="numeric" maxLength={10}
            />
            {form.mobile.length > 0 && !mobileValid && (
              <div style={errorTextStyle}>Enter a valid 10-digit mobile number</div>
            )}
          </div>
          <MasterDropdownField master="education" label="Qualification" value={form.qualification} onChange={setField("qualification")} />
          <div>
            <label style={labelStyle}>Institute</label>
            <input style={inputStyle} value={form.institute} onChange={set("institute")} placeholder="College / University" />
          </div>
          <div>
            <label style={labelStyle}>Passing Year</label>
            <input
              style={inputStyle} value={form.passingYear} onChange={setPassingYear}
              placeholder="2024" inputMode="numeric" maxLength={4}
            />
            {!passingYearValid && (
              <div style={errorTextStyle}>Enter a valid 4-digit year</div>
            )}
          </div>
          <div>
            <label style={labelStyle}>% / CGPA</label>
            <input style={inputStyle} value={form.percentageOrCgpa} onChange={set("percentageOrCgpa")} placeholder="8.5 CGPA" />
          </div>
          <MasterDropdownField master="position" label="Position Applied" value={form.positionApplied} onChange={setField("positionApplied")} />
          <div>
            <label style={labelStyle}>Source</label>
            <input style={inputStyle} value={form.source} onChange={set("source")} placeholder="Referral / Job Portal / Walk-in" />
          </div>
          <div>
            <label style={labelStyle}>Location</label>
            <input style={inputStyle} value={form.location} onChange={set("location")} placeholder="City, State" />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={labelStyle}>Resume (PDF)</label>
            <label style={{
              display: "flex", alignItems: "center", gap: 10, border: `1px dashed ${T.borderStrong}`,
              borderRadius: 10, padding: "12px 14px", cursor: "pointer", fontSize: 13.5, color: T.ink2,
            }}>
              <FaFileUpload color={T.primary} />
              {resumeFile ? resumeFile.name : candidate.resumeOriginalFileName ? `Current: ${candidate.resumeOriginalFileName} (click to replace)` : "Click to choose a PDF file"}
              <input type="file" accept="application/pdf" hidden onChange={(e) => setResumeFile(e.target.files?.[0] || null)} />
            </label>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 24 }}>
          
          <button
            type="button"
            disabled={saving || !canSave}
            onClick={handleSave}
            style={{ ...primaryBtnStyle, opacity: saving || !canSave ? 0.55 : 1, cursor: saving || !canSave ? "not-allowed" : "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

const TIME_PERIOD_OPTIONS = [{ value: "AM", label: "AM" }, { value: "PM", label: "PM" }];

const timeSegmentInputStyle = {
  width: 40, border: "none", outline: "none", background: "transparent",
  textAlign: "center", fontSize: 13.5, fontFamily: T.fontBody, color: T.ink1, padding: "10px 4px",
};

// Hour/Minute are free-typed (any exact minute, not locked to a fixed list
// of options) with AM/PM as a small dropdown — instead of a native <input
// type="time">, whose 12-hour-vs-24-hour display (and whether AM/PM even
// shows) is decided by the browser's locale, not anything we control.
// Typing stays uncommitted (local state) until blur so partial input like
// "1" while typing "12" isn't clamped mid-keystroke. Value/onChange are
// always 24-hour "HH:mm".
function TimePicker({ value, onChange }) {
  const parsed = useMemo(() => {
    if (!value) return { hour12: "12", minute: "00", period: "AM" };
    const [h, m] = value.split(":").map((n) => parseInt(n, 10));
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return { hour12: String(h12).padStart(2, "0"), minute: String(m).padStart(2, "0"), period };
  }, [value]);

  const [hourText, setHourText] = useState(parsed.hour12);
  const [minuteText, setMinuteText] = useState(parsed.minute);

  useEffect(() => setHourText(parsed.hour12), [parsed.hour12]);
  useEffect(() => setMinuteText(parsed.minute), [parsed.minute]);

  const commit = (h12, m, period) => {
    let h = parseInt(h12, 10) % 12;
    if (period === "PM") h += 12;
    onChange(`${String(h).padStart(2, "0")}:${m}`);
  };

  const handleHourBlur = () => {
    let n = parseInt(hourText, 10);
    if (isNaN(n) || n < 1) n = 12;
    if (n > 12) n = 12;
    const clamped = String(n).padStart(2, "0");
    setHourText(clamped);
    commit(clamped, minuteText, parsed.period);
  };

  const handleMinuteBlur = () => {
    let n = parseInt(minuteText, 10);
    if (isNaN(n) || n < 0) n = 0;
    if (n > 59) n = 59;
    const clamped = String(n).padStart(2, "0");
    setMinuteText(clamped);
    commit(hourText, clamped, parsed.period);
  };

  return (
    <div style={{
      display: "flex", alignItems: "stretch", border: `1px solid ${T.border}`, borderRadius: 10,
      background: T.white, width: "fit-content",
    }}>
      <input
        value={hourText}
        onChange={(e) => setHourText(digitsOnly(e.target.value, 2))}
        onBlur={handleHourBlur}
        placeholder="HH"
        style={timeSegmentInputStyle}
      />
      <div style={{ display: "flex", alignItems: "center", color: T.ink3, fontWeight: 700 }}>:</div>
      <input
        value={minuteText}
        onChange={(e) => setMinuteText(digitsOnly(e.target.value, 2))}
        onBlur={handleMinuteBlur}
        placeholder="MM"
        style={timeSegmentInputStyle}
      />
      <div style={{ width: 1, background: T.border }} />
      <div style={{ width: 66 }}>
        <PillSelect variant="plain" value={parsed.period} options={TIME_PERIOD_OPTIONS} onChange={(p) => commit(hourText, minuteText, p)} placeholder="AM/PM" />
      </div>
    </div>
  );
}

// Dropdown of active employees for "who is conducting this" (Test or
// Interview) instead of free-typed text — pulled from the same lightweight
// endpoint HR already uses elsewhere (id/name/designation only). Emits both
// the display name AND that employee's id (onChange(name, id)) — the id is
// what lets the backend look up their email later (e.g. to notify them or
// book their calendar), not just show a name string. The current value is
// kept selectable even if it doesn't match anyone in the active list (id
// comes back null in that case), so editing an older entry — an employee
// since deactivated, or a name typed before this dropdown existed — doesn't
// silently blank out what was already saved.
function AssignedEmployeeField({ label, value, onChange }) {
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    api.getActiveEmployees().then((list) => setEmployees(list || [])).catch(() => {});
  }, []);

  const options = employees.map((e) => {
    const name = `${e.firstName || ""} ${e.lastName || ""}`.trim();
    return { value: name, label: name, employeeId: e.id };
  });
  if (value && !options.some((o) => o.value === value)) {
    options.unshift({ value, label: value, employeeId: null });
  }

  const handleChange = (name) => {
    const opt = options.find((o) => o.value === name);
    onChange(name, opt?.employeeId ?? null);
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      <PillSelect value={value} options={options} onChange={handleChange} placeholder="Select employee" />
    </div>
  );
}

// Config for the two simple name-only masters (Education, Position) that
// back the Qualification/Position Applied dropdowns below — keeps the
// generic modal/field components from having to know which master they're
// managing. Each gets its own icon + accent color so the two manage modals
// (and the toolbar buttons that open them) read as distinct at a glance.
const MASTER_CONFIG = {
  education: {
    title: "Education", icon: FaGraduationCap,
    accent: T.purple, accentSoft: T.purpleSoft,
    list: api.getEducations, add: api.addEducation, update: api.updateEducation, remove: api.deleteEducation,
  },
  position: {
    title: "Position", icon: FaBriefcase,
    accent: T.cyan, accentSoft: T.cyanSoft,
    list: api.getPositions, add: api.addPosition, update: api.updatePosition, remove: api.deletePosition,
  },
};

/* ── Manage-list modal ── add/edit/delete for a simple name-only master
   (Education, Position), opened from its toolbar button so admins never
   have to leave the candidate form to add a missing option. ── */
function MasterListModal({ master, onClose, onChanged }) {
  const cfg = MASTER_CONFIG[master];
  const Icon = cfg.icon;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    cfg.list().then((list) => setItems(list || [])).catch(() => toast.error(`Failed to load ${cfg.title.toLowerCase()} list`)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await cfg.add(newName.trim());
      setNewName("");
      load();
      onChanged();
    } catch (e) {
      toast.error(e.message || `Failed to add ${cfg.title.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await cfg.update(id, editName.trim());
      setEditingId(null);
      load();
      onChanged();
    } catch (e) {
      toast.error(e.message || `Failed to update ${cfg.title.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete this ${cfg.title.toLowerCase()} entry?`)) return;
    try {
      await cfg.remove(id);
      load();
      onChanged();
    } catch (e) {
      toast.error(e.message || `Failed to delete ${cfg.title.toLowerCase()}`);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 460, maxHeight: "84vh", padding: 0, display: "flex", flexDirection: "column" }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "20px 24px", borderBottom: `1px solid ${T.border}`, flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 11, background: cfg.accentSoft,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <Icon size={17} color={cfg.accent} />
            </div>
            <div>
              <div style={{ fontFamily: T.fontDisplay, fontSize: 16.5, fontWeight: 700, color: T.ink1 }}>Manage {cfg.title}</div>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 1 }}>
                {loading ? "Loading…" : `${items.length} ${items.length === 1 ? "entry" : "entries"}`}
              </div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ padding: "18px 24px 0", flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={`New ${cfg.title.toLowerCase()} name…`}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            />
            <button
              type="button"
              disabled={saving || !newName.trim()}
              onClick={handleAdd}
              style={{
                ...primaryBtnStyle, display: "flex", alignItems: "center", gap: 6,
                background: cfg.accent, opacity: saving || !newName.trim() ? 0.5 : 1,
                cursor: saving || !newName.trim() ? "not-allowed" : "pointer",
              }}
            >
              <FaPlus size={11} /> Add
            </button>
          </div>
        </div>

        <div className="plc-scroll" style={{ padding: "0 24px 24px", overflowY: "auto", minHeight: 0, flex: 1 }}>
          {loading ? (
            <Spinner padding="30px 0" />
          ) : items.length === 0 ? (
            <div style={{ textAlign: "center", padding: "36px 0" }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%", background: cfg.accentSoft, margin: "0 auto 12px",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon size={17} color={cfg.accent} />
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink2 }}>No {cfg.title.toLowerCase()} entries yet</div>
              <div style={{ fontSize: 12, color: T.ink3, marginTop: 3 }}>Add one above to get started</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {items.map((item) => (
                <div
                  key={item.id}
                  className="plc-master-row"
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "9px 10px 9px 12px",
                    borderRadius: 10, border: `1px solid ${T.border}`, background: T.white,
                    transition: "border-color .12s ease, box-shadow .12s ease",
                  }}
                >
                  {editingId === item.id ? (
                    <>
                      <input
                        style={{ ...inputStyle, flex: 1, padding: "7px 10px" }}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") handleUpdate(item.id); if (e.key === "Escape") setEditingId(null); }}
                        autoFocus
                      />
                      <IconBtn onClick={() => handleUpdate(item.id)} title="Save" color={T.green} bg={T.greenSoft}><FaCheck size={11} /></IconBtn>
                      <IconBtn onClick={() => setEditingId(null)} title="Cancel" color={T.ink3} bg="#f1f5f9"><FaTimes size={11} /></IconBtn>
                    </>
                  ) : (
                    <>
                      <div style={{
                        width: 28, height: 28, borderRadius: 8, background: cfg.accentSoft, flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 11, fontWeight: 700, color: cfg.accent, fontFamily: T.fontDisplay,
                      }}>
                        {item.name.slice(0, 2).toUpperCase()}
                      </div>
                      <span style={{ flex: 1, fontSize: 13.5, color: T.ink1, fontWeight: 500 }}>{item.name}</span>
                      <IconBtn onClick={() => { setEditingId(item.id); setEditName(item.name); }} title="Edit" color={T.ink2} bg="#f1f5f9"><FaPen size={11} /></IconBtn>
                      <IconBtn onClick={() => handleDelete(item.id)} title="Delete" color={T.danger} bg={T.dangerSoft}><FaTrash size={11} /></IconBtn>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Small circular icon-only action button — hover fills in with `bg`, shared
// by the manage-list rows (edit / delete / save / cancel).
function IconBtn({ onClick, title, color, bg, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        width: 28, height: 28, borderRadius: 8, border: "none", background: "transparent",
        color, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, transition: "background .12s ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = bg)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {children}
    </button>
  );
}

// Dropdown for Qualification (master: "education") / Position Applied
// (master: "position") — same "keep the current value selectable even if
// it's not in the master list" pattern as AssignedEmployeeField, so an
// older candidate's free-typed value (from before this dropdown existed)
// doesn't silently disappear when the form re-renders. List management
// lives elsewhere (the "Education"/"Position" toolbar buttons open
// MasterListModal directly), so this just reads the current list.
function MasterDropdownField({ master, label, value, onChange }) {
  const cfg = MASTER_CONFIG[master];
  const [items, setItems] = useState([]);

  useEffect(() => {
    cfg.list().then((list) => setItems(list || [])).catch(() => {});
  }, [cfg]);

  const options = items.map((i) => ({ value: i.name, label: i.name }));
  if (value && !options.some((o) => o.value === value)) {
    options.unshift({ value, label: value });
  }

  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <PillSelect value={value} options={options} onChange={onChange} placeholder={`Select ${cfg.title.toLowerCase()}`} />
    </div>
  );
}

/* ── Stage action modal (schedule test / move to interview / add round) ──
   Logistics only — round label, date, assigned employee, remarks. No
   score/outcome here: those aren't known yet at scheduling time, and are
   filled in later on this SAME entry via LogResultModal rather than a
   second, duplicate entry. ── */
function StageActionModal({ candidateId, stage, title, showRoundLabel, onClose, onDone }) {
  const [roundLabel, setRoundLabel] = useState("");
  // Pre-filled to today at 10:00 AM — the common case is scheduling same-day
  // or "just pick a normal slot," so this saves the admin two clicks; both
  // the date and time stay fully editable before Save.
  const [scheduledDate, setScheduledDate] = useState(() => joinDateTime(todayDateString(), "10:00"));
  const [interviewer, setInterviewer] = useState("");
  const [interviewerId, setInterviewerId] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const datePart = splitDateTime(scheduledDate).date;
  const timePart = splitDateTime(scheduledDate).time;
  const dateIsPast = Boolean(datePart) && datePart < todayDateString();
  const timeMissing = Boolean(datePart) && !timePart;
  const canSave = !dateIsPast && !timeMissing;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.addStage(candidateId, {
        stage, roundLabel: roundLabel || null, scheduledDate: scheduledDate || null,
        interviewer: interviewer || null, interviewerId, remarks: remarks || null,
      });
      toast.success("Stage updated");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to update stage");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 440 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>{title}</span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        {showRoundLabel && (
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Round Label</label>
            <input style={inputStyle} value={roundLabel} onChange={(e) => setRoundLabel(e.target.value)} placeholder="e.g. Technical Round 1, HR Round" />
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Scheduled Date &amp; Time</label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              type="date"
              min={todayDateString()}
              style={{ ...inputStyle, flex: "1 1 150px", minWidth: 140 }}
              value={datePart}
              onChange={(e) => setScheduledDate(joinDateTime(e.target.value, splitDateTime(scheduledDate).time))}
            />
            <TimePicker
              value={splitDateTime(scheduledDate).time}
              onChange={(t) => setScheduledDate(joinDateTime(datePart, t))}
            />
          </div>
          {dateIsPast && <div style={errorTextStyle}>Scheduled date can't be in the past</div>}
          {!dateIsPast && timeMissing && <div style={errorTextStyle}>Please select a time</div>}
        </div>

        <AssignedEmployeeField
          label={stage === "TEST" ? "Tester" : "Interviewer"}
          value={interviewer}
          onChange={(name, id) => { setInterviewer(name); setInterviewerId(id); }}
        />

        <div style={{ marginBottom: 6 }}>
          <label style={labelStyle}>Remarks</label>
          <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Any notes…" />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 18 }}>
          <button
            type="button"
            disabled={saving || !canSave}
            onClick={handleSave}
            style={{ ...primaryBtnStyle, opacity: saving || !canSave ? 0.55 : 1, cursor: saving || !canSave ? "not-allowed" : "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ── Reschedule modal ── edits ONLY the logistics of an existing Test/
   Interview entry (round label / date / interviewer) — kept separate from
   LogResultModal below so correcting a date doesn't put the score/outcome/
   feedback fields in play, and vice versa. ── */
function RescheduleEntryModal({ candidateId, entry, title, showRoundLabel, onClose, onDone }) {
  const [roundLabel, setRoundLabel] = useState(entry.roundLabel || "");
  const [scheduledDate, setScheduledDate] = useState(entry.scheduledDate ? entry.scheduledDate.slice(0, 16) : "");
  const [interviewer, setInterviewer] = useState(entry.interviewer || "");
  const [interviewerId, setInterviewerId] = useState(entry.interviewerId || null);
  const [saving, setSaving] = useState(false);

  const datePart = splitDateTime(scheduledDate).date;
  const timePart = splitDateTime(scheduledDate).time;
  const timeMissing = Boolean(datePart) && !timePart;
  const canSave = !timeMissing;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.updateStageEntry(candidateId, entry.id, {
        roundLabel: roundLabel || null, scheduledDate: scheduledDate || null,
        interviewer: interviewer || null, interviewerId,
      });
      toast.success("Schedule updated");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to update schedule");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 440 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>{title}</span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        {showRoundLabel && (
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Round Label</label>
            <input style={inputStyle} value={roundLabel} onChange={(e) => setRoundLabel(e.target.value)} placeholder="e.g. Technical Round 1, HR Round" />
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Scheduled Date &amp; Time</label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              type="date"
              style={{ ...inputStyle, flex: "1 1 150px", minWidth: 140 }}
              value={datePart}
              onChange={(e) => setScheduledDate(joinDateTime(e.target.value, timePart))}
            />
            <TimePicker
              value={timePart}
              onChange={(t) => setScheduledDate(joinDateTime(datePart, t))}
            />
          </div>
          {timeMissing && <div style={errorTextStyle}>Please select a time</div>}
        </div>

        <AssignedEmployeeField
          label={entry.stage === "TEST" ? "Tester" : "Interviewer"}
          value={interviewer}
          onChange={(name, id) => { setInterviewer(name); setInterviewerId(id); }}
        />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 18 }}>
          <button
            type="button"
            disabled={saving || !canSave}
            onClick={handleSave}
            style={{ ...primaryBtnStyle, opacity: saving || !canSave ? 0.55 : 1, cursor: saving || !canSave ? "not-allowed" : "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ── Log Result modal ── logs (or later corrects) ONLY the outcome of an
   existing Test/Interview entry — score, outcome, remarks (logistics
   notes) and feedback (the interviewer/tester's actual write-up on the
   candidate). Scheduling fields live in RescheduleEntryModal above instead,
   so fixing a score never puts the date/interviewer at risk of an
   accidental edit, and vice versa. ── */
function LogResultModal({ candidateId, entry, title, onClose, onDone }) {
  const [score, setScore] = useState(entry.score || "");
  const [outcome, setOutcome] = useState(entry.outcome || "");
  const [remarks, setRemarks] = useState(entry.remarks || "");
  const [feedback, setFeedback] = useState(entry.feedback || "");
  const [ratings, setRatings] = useState(() => {
    const r = {};
    INTERVIEW_PARAMETERS.forEach((p) => { r[p.key] = entry.evaluation?.[p.key] ?? null; });
    return r;
  });
  const [saving, setSaving] = useState(false);

  const isInterview = entry.stage === "INTERVIEW";
  const setRating = (key) => (v) => setRatings((r) => ({ ...r, [key]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateStageEntry(candidateId, entry.id, {
        score: score || null, outcome: outcome || null, remarks: remarks || null, feedback: feedback || null,
        ...(isInterview ? ratings : {}),
      });
      toast.success("Result saved");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to save result");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 440, maxHeight: "88vh", overflowY: "auto" }} className="plc-scroll">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>{title}</span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Score / Rating</label>
          <input style={inputStyle} value={score} onChange={(e) => setScore(e.target.value)} placeholder="e.g. 78/100 or 4/5" />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Outcome</label>
          <PillSelect value={outcome} options={[{ value: "", label: "Not decided yet" }, ...OUTCOME_OPTIONS]} onChange={setOutcome} placeholder="Not decided yet" />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Feedback</label>
          <textarea rows={4} style={{ ...inputStyle, resize: "vertical" }} value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Interviewer/tester's write-up on the candidate's performance…" />
        </div>

        <div style={{ marginBottom: isInterview ? 14 : 6 }}>
          <label style={labelStyle}>Remarks</label>
          <textarea rows={2} style={{ ...inputStyle, resize: "vertical" }} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Any logistics notes…" />
        </div>

        {isInterview && (
          <div style={{ marginBottom: 6 }}>
            <label style={labelStyle}>Scorecard</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {INTERVIEW_PARAMETERS.map((p) => (
                <div key={p.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: T.ink2 }}>{p.label}</span>
                  <StarRating value={ratings[p.key]} onChange={setRating(p.key)} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 18 }}>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            style={{ ...primaryBtnStyle, opacity: saving ? 0.55 : 1, cursor: saving ? "not-allowed" : "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ── Scorecard modal ── read-only view of all six Interview evaluation
   parameters, opened via the eye icon next to the average-rating summary
   in the timeline (which only shows the average, not the breakdown). ── */
function ScorecardModal({ entry, onClose }) {
  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 400, maxHeight: "88vh", overflowY: "auto" }} className="plc-scroll">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>Interview Scorecard</span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>
        {entry.roundLabel && <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 16 }}>{entry.roundLabel}</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {INTERVIEW_PARAMETERS.map((p) => (
            <div key={p.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 13, color: T.ink2 }}>{p.label}</span>
              <StarRating value={entry.evaluation?.[p.key]} readOnly size={15} />
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          <button type="button" onClick={onClose} style={textBtnStyle}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ── Screening modal ── a single, immediate decision (Shortlisted / Not
   Shortlisted) — unlike Test/Interview there's no "schedule then complete"
   two-step flow here, since screening isn't something with a date/time to
   schedule. Doubles as the edit form when `entry` is passed (re-opened via
   the pencil icon on an existing Screening timeline entry). ── */
function ScreeningModal({ candidateId, entry, onClose, onDone }) {
  const isEdit = Boolean(entry);
  const [outcome, setOutcome] = useState(entry?.outcome || "PASSED");
  const [remarks, setRemarks] = useState(entry?.remarks || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        await api.updateStageEntry(candidateId, entry.id, { outcome, remarks: remarks || null });
      } else {
        await api.addStage(candidateId, { stage: "SCREENING", outcome, remarks: remarks || null });
      }
      toast.success(isEdit ? "Screening decision updated" : "Screening recorded");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to save screening decision");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 420 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>
            {isEdit ? "Edit Screening Decision" : "Screen Candidate"}
          </span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Decision</label>
          <PillSelect value={outcome} options={SCREENING_OUTCOME_OPTIONS} onChange={setOutcome} placeholder="Select decision" />
        </div>

        <div style={{ marginBottom: 6 }}>
          <label style={labelStyle}>Remarks</label>
          <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Any notes…" />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 18 }}>
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            style={{ ...primaryBtnStyle, opacity: saving ? 0.55 : 1, cursor: saving ? "not-allowed" : "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function findPendingEntry(stageHistory, stage) {
  const matches = stageHistory.filter((h) => h.stage === stage && !h.outcome);
  return matches.length ? matches[matches.length - 1] : null;
}

/* ── Final result modal ──────────────────────────────────────────────── */
function ResultModal({ candidateId, title = "Set Final Result", initialResult = "SELECTED", onClose, onDone }) {
  const [result, setResultVal] = useState(initialResult);
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.setResult(candidateId, { result, remarks: remarks || null });
      toast.success("Result recorded");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to set result");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: 420 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1 }}>{title}</span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Result</label>
          <PillSelect value={result} options={RESULT_OPTIONS} onChange={setResultVal} placeholder="Select result" />
        </div>

        <div style={{ marginBottom: 6 }}>
          <label style={labelStyle}>Remarks</label>
          <textarea rows={3} style={{ ...inputStyle, resize: "vertical" }} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Any notes…" />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 18 }}>
          
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            style={{ ...primaryBtnStyle, opacity: saving ? 0.55 : 1, cursor: saving ? "not-allowed" : "pointer" }}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// Converts a plain-text email body (blank-line separated paragraphs, with
// the signature block as its own trailing paragraph) into escaped HTML
// paragraphs for the styled email template below. Single line breaks
// inside a paragraph become <br/>; the last paragraph (signature) gets no
// bottom margin since the divider/footer follow immediately after it.
function bodyTextToHtmlParagraphs(text) {
  const paragraphs = String(text || "").split(/\n{2,}/).filter((p) => p.trim() !== "");
  return paragraphs
    .map((p, i) => {
      const isLast = i === paragraphs.length - 1;
      // Escape first (so raw <, >, & in the admin's text stay literal), then
      // apply a minimal **bold** markdown pass — the plain textarea has no
      // rich-text formatting, so this is the only way to bold specific words
      // (e.g. the sign-off's team/company name) in the rendered email.
      const escaped = escapeHtml(p)
        .replace(/\n/g, "<br/>")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      return `<p style="margin:0 0 ${isLast ? 0 : 16}px;">${escaped}</p>`;
    })
    .join("");
}

// Wraps the admin-edited subject/body into a polished, table-based HTML
// email — inline styles throughout (email clients strip <style> blocks and
// ignore CSS variables), a branded header, a legible content area, and a
// standard "system generated / do not reply" notice in the footer. This is
// the HTML actually handed to the backend for sending; the plain-text
// version stays in the modal purely so the admin has something simple to
// edit and it can be used as a fallback (text/plain part) by the mailer.
// One distinct header color per template (not just per stage) — each of
// the 12 templates gets its own hue so the admin/candidate can tell at a
// glance what kind of email this is, loosely grouped by sentiment (green =
// good news, red/orange/pink = rejection-family, amber/stone = pending,
// purple/teal/blue = neutral/informational).
const EMAIL_TEMPLATE_COLORS = {
  APPLICATION_RECEIVED:      { bg: "#7c3aed", gradient: "linear-gradient(135deg,#a78bfa,#6d28d9)" }, // purple
  SCREENING_SHORTLISTED:     { bg: "#0d9488", gradient: "linear-gradient(135deg,#2dd4bf,#0f766e)" }, // teal
  SCREENING_NOT_SHORTLISTED: { bg: "#e11d48", gradient: "linear-gradient(135deg,#fb7185,#9f1239)" }, // rose
  SCHEDULED:                 { bg: "#2563eb", gradient: "linear-gradient(135deg,#60a5fa,#1d4ed8)" }, // blue
  RESULT_PASSED:             { bg: "#16a34a", gradient: "linear-gradient(135deg,#4ade80,#15803d)" }, // green
  RESULT_FAILED:             { bg: "#dc2626", gradient: "linear-gradient(135deg,#f87171,#b91c1c)" }, // red
  RESULT_ON_HOLD:            { bg: "#d97706", gradient: "linear-gradient(135deg,#fbbf24,#b45309)" }, // amber
  FINAL_SELECTED:            { bg: "#ca8a04", gradient: "linear-gradient(135deg,#facc15,#a16207)" }, // gold
  FINAL_REJECTED:            { bg: "#c2410c", gradient: "linear-gradient(135deg,#fb923c,#9a3412)" }, // orange
  FINAL_ON_HOLD:             { bg: "#78716c", gradient: "linear-gradient(135deg,#a8a29e,#57534e)" }, // stone
  FINAL_OFFER_DECLINED:      { bg: "#475569", gradient: "linear-gradient(135deg,#94a3b8,#334155)" }, // slate
  FINAL_NOT_SHORTLISTED:     { bg: "#be185d", gradient: "linear-gradient(135deg,#f472b6,#831843)" }, // pink
};
const DEFAULT_EMAIL_COLOR = { bg: "#4f46e5", gradient: "linear-gradient(135deg,#6366f1,#4338ca)" }; // indigo fallback

function buildEmailHtml({ candidate, entry, subject, body, companyName, companyEmail, templateKey }) {
  const stageLabel = "Application Update"; // header badge — same wording on every template, regardless of stage
  const position =  "Talent Acquisition";
  const senderName = companyName ? `${companyName}` : "Recruitment Team";
  const paragraphsHtml = bodyTextToHtmlParagraphs(body);
  const year = new Date().getFullYear();
  const { bg: headerBg, gradient: headerGradient } = EMAIL_TEMPLATE_COLORS[templateKey] || DEFAULT_EMAIL_COLOR;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<meta http-equiv="X-UA-Compatible" content="IE=edge" />
<title>${escapeHtml(subject)}</title>
<style>*{box-sizing:border-box;}body{margin:0;}</style>
</head>
<body style="margin:0;padding:0;background-color:#f4f6f8;-webkit-text-size-adjust:100%;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f4f6f8;">
    <tr>
      <td align="center" style="padding:36px 16px;">
        <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;width:100%;background-color:#ffffff;border-radius:14px;overflow:hidden;box-shadow:0 4px 18px rgba(16,24,40,0.08);font-family:'Segoe UI',Helvetica,Arial,sans-serif;">

          <!-- header -->
          <tr>
            <td style="background-color:${headerBg};background-image:${headerGradient};padding:30px 36px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr>
                  <td style="font-size:19px;font-weight:700;color:#ffffff;letter-spacing:.2px;">${escapeHtml(senderName)}</td>
                  <td align="right" style="white-space:nowrap;">
                    <span style="display:inline-block;font-size:11px;font-weight:700;color:#ffffff;background-color:rgba(255,255,255,0.18);border-radius:20px;padding:5px 12px;white-space:nowrap;">${escapeHtml(stageLabel.toUpperCase())}</span>
                  </td>
                </tr>
              </table>
              <div style="font-size:13px;color:rgba(255,255,255,0.85);margin-top:6px;">${escapeHtml(position)}</div>
            </td>
          </tr>

          <!-- greeting strip -->
          <tr>
            <td style="padding:28px 36px 0;">
              <div style="font-size:15px;font-weight:700;color:#101828;">${escapeHtml(subject)}</div>
            </td>
          </tr>

          <!-- body -->
          <tr>
            <td style="padding:16px 36px 32px;">
              <div style="font-size:14px;line-height:1.7;color:#344054;">
                ${paragraphsHtml}
              </div>
            </td>
          </tr>

          <!-- divider -->
          <tr>
            <td style="padding:0 36px;">
              <div style="border-top:1px solid #e4e8ee;"></div>
            </td>
          </tr>

          <!-- footer -->
          <tr>
            <td style="padding:22px 36px 30px;">
              <div style="font-size:11.5px;color:#98a2b3;text-align:center;line-height:1.7;">
                This is an automated email. Please do not reply to this message.<br/>
                ${companyEmail ? `For recruitment-related enquiries, please contact us at <a href="mailto:${escapeHtml(companyEmail)}" style="color:${headerBg};text-decoration:none;">${escapeHtml(companyEmail)}</a><br/>` : ""}
                &copy; ${year} ${escapeHtml(senderName)} &middot; All rights reserved.
              </div>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/* ── Email templates ── manual, per-entry only. Nothing here is ever sent
   automatically — these just seed the subject/body so the admin has a
   starting point to edit before sending. ── */
function buildEmailTemplates(candidate, entry, companyName, companyAddress) {
  const name = candidate.name;
  const position = candidate.positionApplied || "the role";
  const round = entry.roundLabel ? ` (${entry.roundLabel})` : "";
  const stageLabel = entry.stage === "TEST" ? "Test" : entry.stage === "INTERVIEW" ? "Interview" : "Result";
  const scheduled = entry.scheduledDate ? formatDateTime(entry.scheduledDate) : "the scheduled date";
  const sign = companyName
    ? `\n\nKind regards,\n**Talent Acquisition Team**\n**${companyName}**`
    : "\n\nKind regards,\n**Talent Acquisition Team**";
  const org = companyName || "Balvirt";

  if (entry.stage === "ENQUIRY") {
    return [
      {
        key: "APPLICATION_RECEIVED",
        label: "Application Received",
        subject: `Application Received – ${position}`,
        body: `Dear ${name},\n\nThank you for applying for the ${position} role at ${org}.\n\nWe have successfully received your application and appreciate the time and effort you invested in sharing your experience with us.\n\nOur Talent Acquisition team is currently reviewing all applications. If your qualifications match our requirements, we will contact you regarding the next steps in the recruitment process.\n\nWe appreciate your interest in becoming part of ${org} and wish you the best throughout the selection process.${sign}`,
      },
    ];
  }

  if (entry.stage === "SCREENING") {
    return [
      {
        key: "SCREENING_SHORTLISTED",
        label: "Screening — Shortlisted",
        subject: `You've Been Shortlisted for the Assessment – ${position}`,
        body: `Dear ${name},\n\nThank you for your interest in the ${position} position at ${org}.\n\nWe are pleased to inform you that your profile has been shortlisted based on our initial review of your application.\n\nAs the next step in our recruitment process, you will be required to complete an assessment. Our recruitment team will contact you shortly with the assessment details and instructions.\n\nCandidates who successfully clear the assessment will be invited to the interview stage.\n\nThank you for your interest in joining ${org}. We look forward to your participation.${sign}`,
      },
      {
        key: "SCREENING_NOT_SHORTLISTED",
        label: "Screening — Not Shortlisted",
        subject: `Application Update — ${position}`,
        body: `Dear ${name},\n\nThank you for your interest in the ${position} position at ${org} and for taking the time to apply.\n\nAfter carefully reviewing your application, we have decided not to move forward with your application at this stage. This was not an easy decision, and we sincerely appreciate the time and effort you invested in the application process.\n\nWe truly appreciate your interest in ${org} and encourage you to apply for future opportunities that match your skills and experience.\n\nWe wish you every success in your career and future endeavors.${sign}`,
      },
    ];
  }

  if (entry.stage === "TEST" || entry.stage === "INTERVIEW") {
    return [
      {
        key: "SCHEDULED",
        label: `${stageLabel} Scheduled`,
        subject: `${stageLabel} Scheduled — ${position}`,
        body: `Dear ${name},\n\nWe are pleased to inform you that your ${stageLabel.toLowerCase()}${round} for the position of ${position} has been scheduled.\n\nDate: ${scheduled}${companyAddress ? `\nVenue: ${companyAddress}` : ""}\n\nFor additional details, our team will contact you shortly.\n\nPlease be available on time. Let us know if you have any questions.${sign}`,
      },
      {
        key: "RESULT_PASSED",
        label: `${stageLabel} Result — Passed`,
        subject: `${stageLabel} Result — ${position}`,
        body: `Dear ${name},\n\nCongratulations! You have successfully cleared the ${stageLabel.toLowerCase()}${round} for the position of ${position}.\n\nWe will be in touch shortly regarding the next steps.${sign}`,
      },
      {
        key: "RESULT_FAILED",
        label: `${stageLabel} Result — Not Cleared`,
        subject: `${stageLabel} Result — ${position}`,
        body: entry.stage === "TEST"
          ? `Dear ${name},\n\nThank you for taking the assessment for the ${position} position.\n\nAfter reviewing your test, we regret to inform you that you have not cleared this stage of the selection process. We appreciate the time and effort you invested in completing the assessment.\n\nWe wish you all the best in your career and hope to connect with you for future opportunities.${sign}`
          : `Dear ${name},\n\nThank you for taking the time to attend the ${entry.roundLabel ? `**${entry.roundLabel}** interview` : "interview"} for the **${position}** position.\n\nAfter careful evaluation, we regret to inform you that you have not cleared this interview round, and we will not be moving forward with your application at this stage.\n\nWe sincerely appreciate your time, effort, and interest in joining our team. We wish you every success in your career and hope to connect with you for future opportunities that match your skills and experience.${sign}`,
      },
      {
        key: "RESULT_ON_HOLD",
        label: `${stageLabel} Result — On Hold`,
        subject: `${stageLabel} Result — ${position}`,
        body: `Dear ${name},\n\nThank you for participating in the ${stageLabel.toLowerCase()}${round} for the position of ${position}.\n\nYour application is currently under further consideration, and we truly appreciate your patience during this time.\n\nWe will keep you updated and reach out as soon as a decision has been made.${sign}`,
      },
    ];
  }

  return [
    {
      key: "FINAL_SELECTED",
      label: "Final Result — Offered",
      subject: `Job Offer — ${position}`,
      body: `Dear ${name},\n\nCongratulations! We are delighted to offer you the position of ${position} at our organization.\n\nOur HR team will reach out shortly with further details regarding onboarding.${sign}`,
    },
    {
      key: "FINAL_REJECTED",
      label: "Final Result — Rejected",
      subject: `Application Update — ${position}`,
      body: `Dear ${name},\n\nThank you so much for your interest in the position of ${position} and for the time and effort you invested throughout our selection process.\n\nAfter careful consideration, we have decided to move forward with another candidate whose profile more closely matches our current requirements. Please know this decision is not a reflection of your skills or potential, and we genuinely enjoyed getting to know you through this process.\n\nWe would be glad to consider you for future opportunities that may be a better fit, and we encourage you to stay in touch.\n\nWe sincerely wish you continued success and all the very best in your career ahead.${sign}`,
    },
    {
      key: "FINAL_ON_HOLD",
      label: "Final Result — On Hold",
      subject: `Application Update — ${position}`,
      body: `Dear ${name},\n\nThank you for your patience and continued interest throughout our selection process for the position of ${position}.\n\nYour application is currently under further review, and we sincerely appreciate your understanding during this time.\n\nWe will be sure to update you as soon as a final decision has been made.${sign}`,
    },
    {
      key: "FINAL_OFFER_DECLINED",
      label: "Final Result — Offer Declined",
      subject: `Offer Declined — ${position}`,
      body: `Dear ${name},\n\nThank you for letting us know your decision regarding our offer for the position of ${position}. While we are sorry that we won't have the opportunity to welcome you to our team at this time, we completely understand and respect your decision.\n\nWe truly enjoyed getting to know you through our process and appreciate the time and thought you put into it.\n\nWe would be delighted to stay in touch, and we wish you all the very best in your career ahead.${sign}`,
    },
    {
      key: "FINAL_NOT_SHORTLISTED",
      label: "Final Result — Not Shortlisted",
      subject: `Application Update — ${position}`,
      body: `Dear ${name},\n\nThank you very much for your interest in the position of ${position} and for taking the time to apply.\n\nAfter carefully reviewing your application, we have decided to move forward with other candidates whose profiles more closely align with our current requirements at this time. Please know this decision does not reflect the value of your skills and experience.\n\nWe genuinely appreciate the effort you put into your application and would welcome you to apply for future openings that may be a better match.\n\nWe wish you all the very best in your job search and future career endeavors.${sign}`,
    },
  ];
}

function defaultTemplateKey(entry) {
  if (entry.stage === "ENQUIRY") return "APPLICATION_RECEIVED";
  if (entry.stage === "SCREENING") return entry.outcome === "FAILED" ? "SCREENING_NOT_SHORTLISTED" : "SCREENING_SHORTLISTED";
  if (entry.stage === "RESULT") {
    return {
      SELECTED: "FINAL_SELECTED", REJECTED: "FINAL_REJECTED", ON_HOLD: "FINAL_ON_HOLD",
      OFFER_DECLINED: "FINAL_OFFER_DECLINED", NOT_SHORTLISTED: "FINAL_NOT_SHORTLISTED",
    }[entry.result] || "FINAL_SELECTED";
  }
  if (!entry.outcome) return "SCHEDULED";
  return { PASSED: "RESULT_PASSED", FAILED: "RESULT_FAILED", ON_HOLD: "RESULT_ON_HOLD" }[entry.outcome] || "RESULT_PASSED";
}

/* ── Send Email modal ── admin picks a template, edits subject/body freely,
   then sends. Nothing here fires on its own — every email is a deliberate
   click, and the entry is marked emailSent only once this succeeds. ── */
function SendEmailModal({ candidateId, candidate, entry, onClose, onDone }) {
  const [templateKey, setTemplateKey] = useState(() => defaultTemplateKey(entry));
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const [companyName, setCompanyName] = useState(null); // null = not yet resolved
  const [companyEmail, setCompanyEmail] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");

  useEffect(() => {
    api.getCompanyName()
      .then((data) => {
        setCompanyName(data?.companyName || "");
        setCompanyEmail(data?.email || "");
        setCompanyAddress(data?.address || "");
      })
      .catch(() => setCompanyName("")); // header/signature just fall back to "Recruitment Team" if this fails
  }, []);

  const templates = useMemo(
    () => buildEmailTemplates(candidate, entry, companyName || "", companyAddress),
    [candidate, entry, companyName, companyAddress]
  );

  // Populate the default subject/body once the company name has settled
  // (resolved or failed — either way companyName moves from null to a
  // string) so the initial draft already includes it in the signature,
  // instead of racing the fetch and missing it.
  useEffect(() => {
    if (companyName === null) return;
    const t = templates.find((x) => x.key === defaultTemplateKey(entry));
    if (t) { setSubject(t.subject); setBody(t.body); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName]);

  const handleTemplateChange = (key) => {
    setTemplateKey(key);
    const t = templates.find((x) => x.key === key);
    if (t) { setSubject(t.subject); setBody(t.body); }
  };

  // Recomputed whenever subject/body/companyName/companyEmail change so the
  // live preview panel always reflects the latest edit.
  const htmlPreview = useMemo(
    () => buildEmailHtml({ candidate, entry, subject, body, companyName, companyEmail, templateKey }),
    [candidate, entry, subject, body, companyName, companyEmail, templateKey]
  );

  const handleSend = async () => {
    setSending(true);
    try {
      // Plain-text body stays as the source of truth the admin edits; the
      // branded HTML is generated from it and sent alongside so the
      // backend/mailer can use it as the rich (text/html) part, falling
      // back to plain `body` for clients that don't render HTML mail.
      await api.sendStageEmail(candidateId, entry.id, { subject, body, html: htmlPreview });
      onDone();
      setJustSent(true);
      setTimeout(onClose, 1800); // let the success animation play before closing
    } catch (e) {
      toast.error(e.message || "Failed to send email");
      setSending(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{`
        @media (max-width: 760px) {
          .plc-email-grid { grid-template-columns: 1fr !important; }
        }
        .plc-check-circle {
          stroke-dasharray: 210; stroke-dashoffset: 210;
          animation: plc-check-circle-draw .5s ease-out forwards;
        }
        .plc-check-mark {
          stroke-dasharray: 50; stroke-dashoffset: 50;
          animation: plc-check-mark-draw .35s ease-out .45s forwards;
        }
        .plc-check-ring {
          animation: plc-check-ring-pulse 1s ease-out .1s;
        }
        @keyframes plc-check-circle-draw { to { stroke-dashoffset: 0; } }
        @keyframes plc-check-mark-draw { to { stroke-dashoffset: 0; } }
        @keyframes plc-check-ring-pulse {
          0% { box-shadow: 0 0 0 0 rgba(22,163,74,0.35); }
          100% { box-shadow: 0 0 0 14px rgba(22,163,74,0); }
        }
      `}</style>
      <div style={justSent
        ? { ...modalStyle, maxWidth: 360, width: "90%", display: "flex", flexDirection: "column" }
        : { ...modalStyle, maxWidth: 920, width: "94%", maxHeight: "88vh", overflowY: "auto", display: "flex", flexDirection: "column" }
      }>
        {justSent ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 16px", textAlign: "center" }}>
            <div className="plc-check-ring" style={{ borderRadius: "50%", marginBottom: 14 }}>
              <svg width="48" height="48" viewBox="0 0 72 72">
                <circle className="plc-check-circle" cx="36" cy="36" r="32" fill="none" stroke={T.green} strokeWidth="4" strokeLinecap="round" />
                <path className="plc-check-mark" d="M22 37 L32 47 L50 25" fill="none" stroke={T.green} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div style={{ fontFamily: T.fontDisplay, fontSize: 15, fontWeight: 700, color: T.ink1, marginBottom: 4 }}>Email Sent!</div>
            <div style={{ fontSize: 12, color: T.ink3 }}>Delivered to {candidate.email}</div>
          </div>
        ) : (
          <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexShrink: 0 }}>
          <span style={{ fontFamily: T.fontDisplay, fontSize: 17, fontWeight: 700, color: T.ink1, display: "flex", alignItems: "center", gap: 9 }}>
            <FaEnvelope size={14} color={T.primary} /> Send Email
          </span>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
            <FaTimes />
          </button>
        </div>

        <div style={{ fontSize: 12.5, color: T.ink3, marginBottom: 16 }}>To: {candidate.email}</div>

        <div className="plc-email-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          {/* left — edit */}
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Template</label>
              <PillSelect value={templateKey} options={templates.map((t) => ({ value: t.key, label: t.label }))} onChange={handleTemplateChange} placeholder="Select template" />
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Subject</label>
              <input style={inputStyle} value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <label style={labelStyle}>Body</label>
              <textarea
                rows={14}
                style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5, flex: 1, minHeight: 260 }}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 6 }}>
                Separate paragraphs with a blank line.
              </div>
            </div>
          </div>

          {/* right — live preview, mirrors exactly what gets sent */}
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <label style={labelStyle}>Live Preview</label>
            <div style={{ flex: 1, minHeight: 300, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", background: "#f4f6f8" }}>
              <iframe
                title="Email preview"
                srcDoc={htmlPreview}
                style={{ width: "100%", height: "100%", minHeight: 300, border: "none", display: "block" }}
              />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 20, flexShrink: 0 }}>
          <button
            type="button"
            disabled={sending || !subject.trim() || !body.trim()}
            onClick={handleSend}
            style={{ ...primaryBtnStyle, opacity: sending || !subject.trim() || !body.trim() ? 0.55 : 1, cursor: sending ? "not-allowed" : "pointer" }}
          >
            {sending ? "Sending…" : "Send Email"}
          </button>
          <button type="button" onClick={onClose} style={textBtnStyle}>Cancel</button>
        </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Candidate detail drawer ─────────────────────────────────────────── */
function DetailDrawer({ candidateId, onClose, onChanged }) {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageAction, setStageAction] = useState(null); // { stage, title, showRoundLabel }
  const [logResultTarget, setLogResultTarget] = useState(null); // { entry, title }
  const [rescheduleTarget, setRescheduleTarget] = useState(null); // { entry, title, showRoundLabel }
  const [scorecardTarget, setScorecardTarget] = useState(null); // CandidateStageHistory entry (INTERVIEW)
  const [screeningModal, setScreeningModal] = useState(null); // { entry } — entry null means "create new"
  const [resultModalTarget, setResultModalTarget] = useState(null); // { title, initialResult }
  const [emailTarget, setEmailTarget] = useState(null); // stage history entry
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResumeViewer, setShowResumeViewer] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getById(candidateId);
      setCandidate(data);
    } catch (e) {
      toast.error(e.message || "Failed to load candidate");
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged(); };

  const handlePrint = () => {
    if (!candidate) return;
    const html = buildCandidatePrintHtml(candidate);

    // Print through a hidden same-page iframe instead of window.open — a
    // popup window (even one that "auto-closes") still flashes up as a
    // second window. This way nothing new ever opens; the browser's print
    // dialog appears for the current window, targeting the iframe's content.
    let iframe = document.getElementById("plc-print-frame");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "plc-print-frame";
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 300); // let layout settle before invoking print
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.remove(candidateId);
      toast.success("Candidate deleted");
      setShowDeleteConfirm(false);
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to delete candidate");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={overlayStyle} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: "min(560px, 94vw)", height: "100%", background: T.white, boxShadow: T.shadowMd,
        position: "fixed", top: 0, right: 0, display: "flex", flexDirection: "column",
        fontFamily: T.fontBody, animation: "plc-fade-in .16s ease",
      }}>
        {loading || !candidate ? (
          <Spinner padding="120px 0" />
        ) : (
          <>
            <div style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontFamily: T.fontDisplay, fontSize: 19, fontWeight: 700, color: T.ink1 }}>{candidate.name}</div>
                <div style={{ fontSize: 13, color: T.ink2, marginTop: 4 }}>{candidate.positionApplied || "Position not specified"}</div>
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <StagePill stage={candidate.currentStage} />
                  <ResultPill result={candidate.result} />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button type="button" onClick={() => setShowEditModal(true)} title="Edit candidate" style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
                  <FaPen />
                </button>
                <button type="button" onClick={handlePrint} title="Print candidate details" style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 14, padding: 6 }}>
                  <FaPrint />
                </button>
                <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 16, padding: 6 }}>
                  <FaTimes />
                </button>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }} className="plc-scroll">
              {/* profile */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 18px", marginBottom: 22, fontSize: 13.5 }}>
                <ProfileField label="Email" value={candidate.email} />
                <ProfileField label="Mobile" value={candidate.mobile} />
                <ProfileField label="Qualification" value={candidate.qualification} />
                <ProfileField label="Institute" value={candidate.institute} />
                <ProfileField label="Passing Year" value={candidate.passingYear} />
                <ProfileField label="% / CGPA" value={candidate.percentageOrCgpa} />
                <ProfileField label="Source" value={candidate.source} />
                <ProfileField label="Location" value={candidate.location} />
                <ProfileField label="Added On" value={formatDate(candidate.createdAt)} />
              </div>

              <div style={{ marginBottom: 22 }}>
                {candidate.hasResume ? (
                  <button type="button" onClick={() => setShowResumeViewer(true)} className="plc-btn" style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderRadius: 9,
                    border: `1px solid ${T.border}`, background: T.white, color: T.danger, fontWeight: 600,
                    fontSize: 13.5, cursor: "pointer",
                  }}>
                    <FaFilePdf /> {candidate.resumeOriginalFileName || "View Resume"}
                  </button>
                ) : (
                  <div style={{ fontSize: 12.5, color: T.ink3 }}>No resume uploaded</div>
                )}
              </div>

              {/* stage actions — each forward step is gated behind the
                  preceding stage's own outcome, so you can't screen someone
                  before acknowledging their application, schedule a test
                  before they're shortlisted, or move to interview before
                  they've passed the test. */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                {candidate.currentStage === "ENQUIRY" && (
                  <ActionButton icon={FaUserCheck} color={T.teal} label="Screen Candidate"
                    onClick={() => setScreeningModal({ entry: null })} />
                )}
                {candidate.currentStage === "SCREENING" && (() => {
                  const screeningEntry = candidate.stageHistory.filter((h) => h.stage === "SCREENING").slice(-1)[0];
                  const shortlisted = screeningEntry?.outcome === "PASSED";
                  return shortlisted ? (
                    <ActionButton icon={FaArrowRight} color={T.cyan} label="Schedule Test"
                      onClick={() => setStageAction({ stage: "TEST", title: "Schedule Test", showRoundLabel: false })} />
                  ) : null;
                })()}
                {candidate.currentStage === "TEST" && (() => {
                  const pending = findPendingEntry(candidate.stageHistory, "TEST");
                  const testOutcome = getCurrentStageOutcome(candidate);
                  const passed = testOutcome === "PASSED";
                  return (
                    <>
                      {pending && (
                        <ActionButton icon={FaCheckCircle} color={T.cyan} label="Log Test Result"
                          onClick={() => setLogResultTarget({ entry: pending, title: "Log Test Result" })} />
                      )}
                      {passed && (
                        <ActionButton icon={FaArrowRight} color={T.purple} label="Move to Interview"
                          onClick={() => setStageAction({ stage: "INTERVIEW", title: "Move to Interview", showRoundLabel: true })} />
                      )}
                    </>
                  );
                })()}
                {candidate.currentStage === "INTERVIEW" && (() => {
                  const pending = findPendingEntry(candidate.stageHistory, "INTERVIEW");
                  return (
                    <>
                      {!pending && (
                        <ActionButton icon={FaPlus} color={T.purple} label="Add Interview Round"
                          onClick={() => setStageAction({ stage: "INTERVIEW", title: "Add Interview Round", showRoundLabel: true })} />
                      )}
                      {pending && (
                        <ActionButton icon={FaCheckCircle} color={T.purple} label="Log Interview Result"
                          onClick={() => setLogResultTarget({ entry: pending, title: "Log Interview Result" })} />
                      )}
                    </>
                  );
                })()}
                {candidate.currentStage !== "RESULT" && (
                  <ActionButton icon={FaFlagCheckered} color={T.green} label="Set Final Result"
                    onClick={() => setResultModalTarget({ title: "Set Final Result", initialResult: "SELECTED" })} />
                )}
                {candidate.currentStage === "RESULT" && (
                  <ActionButton icon={FaFlagCheckered} color={T.green} label="Change Result"
                    onClick={() => setResultModalTarget({ title: "Change Result", initialResult: candidate.result || "SELECTED" })} />
                )}
              </div>
              {(() => {
                if (candidate.currentStage === "SCREENING") {
                  const screeningEntry = candidate.stageHistory.filter((h) => h.stage === "SCREENING").slice(-1)[0];
                  if (screeningEntry && screeningEntry.outcome !== "PASSED") {
                    return <div style={{ fontSize: 12, color: T.ink3, marginBottom: 18 }}>Not shortlisted at screening — no further stage action available.</div>;
                  }
                }
                if (candidate.currentStage === "TEST") {
                  const pending = findPendingEntry(candidate.stageHistory, "TEST");
                  const testOutcome = getCurrentStageOutcome(candidate);
                  if (!pending && testOutcome && testOutcome !== "PASSED") {
                    return <div style={{ fontSize: 12, color: T.ink3, marginBottom: 18 }}>Candidate did not pass the test — no further stage action available.</div>;
                  }
                }
                if (candidate.currentStage === "INTERVIEW") {
                  const pending = findPendingEntry(candidate.stageHistory, "INTERVIEW");
                  if (pending) {
                    return <div style={{ fontSize: 12, color: T.ink3, marginBottom: 18 }}>Log this round's result before adding another interview round.</div>;
                  }
                }
                return null;
              })()}

              {/* timeline */}
              <div style={{ fontSize: 11.5, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 12 }}>
                Timeline
              </div>
              <div>
                {candidate.stageHistory.length === 0 ? (
                  <div style={{ fontSize: 13, color: T.ink3 }}>No history yet.</div>
                ) : (
                  candidate.stageHistory.slice().reverse().map((h, i) => (
                    <div key={h.id ?? i} style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: T.primary, marginTop: 4 }} />
                        {i !== candidate.stageHistory.length - 1 && <div style={{ width: 2, flex: 1, background: T.border, marginTop: 4 }} />}
                      </div>
                      <div style={{ paddingBottom: 4, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <StagePill stage={h.stage} />
                          {h.roundLabel && <span style={{ fontSize: 12.5, fontWeight: 600, color: T.ink1 }}>{h.roundLabel}</span>}
                          {h.result && <ResultPill result={h.result} />}
                          {h.outcome && (
                            <span style={{
                              fontSize: 11, fontWeight: 700,
                              color: h.stage === "ENQUIRY" ? T.primaryDark
                                : h.outcome === "FAILED" ? T.danger
                                : h.outcome === "PASSED" ? T.green
                                : T.amber,
                            }}>
                              {h.stage === "ENQUIRY" ? "APPLICATION RECEIVED"
                                : h.stage === "SCREENING" ? (h.outcome === "PASSED" ? "SHORTLISTED" : h.outcome === "FAILED" ? "NOT SHORTLISTED" : h.outcome)
                                : h.outcome}
                            </span>
                          )}
                          {(h.stage === "TEST" || h.stage === "INTERVIEW") && (
                            <>
                              <button
                                type="button"
                                onClick={() => setRescheduleTarget({
                                  entry: h,
                                  title: `Reschedule ${h.stage === "TEST" ? "Test" : "Interview"}`,
                                  showRoundLabel: h.stage === "INTERVIEW",
                                })}
                                title="Reschedule this entry (date / interviewer)"
                                style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 12, padding: 2 }}
                              >
                                <FaCalendarAlt />
                              </button>
                              <button
                                type="button"
                                onClick={() => setLogResultTarget({
                                  entry: h,
                                  title: h.outcome ? `Edit ${h.stage === "TEST" ? "Test" : "Interview"} Result` : `Log ${h.stage === "TEST" ? "Test" : "Interview"} Result`,
                                })}
                                title={h.outcome ? "Edit this result" : "Log this result"}
                                style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 12, padding: 2 }}
                              >
                                <FaPen />
                              </button>
                            </>
                          )}
                          {h.stage === "SCREENING" && (
                            <button
                              type="button"
                              onClick={() => setScreeningModal({ entry: h })}
                              title="Edit screening decision"
                              style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 12, padding: 2 }}
                            >
                              <FaPen />
                            </button>
                          )}
                          {(h.stage === "ENQUIRY" || h.stage === "SCREENING" || h.stage === "TEST" || h.stage === "INTERVIEW" || h.stage === "RESULT") && (
                            <button
                              type="button"
                              onClick={() => setEmailTarget(h)}
                              title="Send email to candidate"
                              style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 12, padding: 2 }}
                            >
                              <FaEnvelope />
                            </button>
                          )}
                          {getAvgInterviewRating(h) != null && (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
                              <div title="Average interview scorecard rating" style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <StarRating value={Math.round(getAvgInterviewRating(h))} readOnly size={12} />
                                <span style={{ fontSize: 11, fontWeight: 700, color: T.ink3 }}>{getAvgInterviewRating(h).toFixed(1)}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => setScorecardTarget(h)}
                                title="View full scorecard"
                                style={{ border: "none", background: "transparent", color: T.ink3, cursor: "pointer", fontSize: 12, padding: 2 }}
                              >
                                <FaEye />
                              </button>
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: T.ink3, fontFamily: T.fontMono, marginTop: 3 }}>{formatDateTime(h.createdAt)}</div>
                        {h.interviewer && <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 3 }}>{h.stage === "TEST" ? "Tester" : "Interviewer"}: {h.interviewer}</div>}
                        {h.score && <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 3 }}>Score: {h.score}</div>}
                        {h.scheduledDate && <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 3 }}>Scheduled: {formatDateTime(h.scheduledDate)}</div>}
                        {h.rescheduleLog && (
                          <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 3, fontStyle: "italic" }}>
                            Previously scheduled: {h.rescheduleLog.split("\n").filter(Boolean).map((d) => formatDateTime(d)).join(" → ")}
                          </div>
                        )}
                        {h.feedback && (
                          <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 3 }}>
                            <span style={{ fontWeight: 600 }}>Feedback:</span> {h.feedback}
                          </div>
                        )}
                        {h.remarks && (
                          <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 3 }}>
                            <span style={{ fontWeight: 600 }}>Remark:</span> {h.remarks}
                          </div>
                        )}
                        {(h.stage === "ENQUIRY" || h.stage === "SCREENING" || h.stage === "TEST" || h.stage === "INTERVIEW" || h.stage === "RESULT") && (
                          <div style={{ fontSize: 11.5, marginTop: 3, color: h.emailSent ? T.green : T.ink3, display: "flex", alignItems: "center", gap: 4 }}>
                            {h.emailSent ? <FaCheckCircle size={10} /> : <FaEnvelope size={10} />}
                            {h.emailSent ? `Email sent · ${formatDateTime(h.emailSentAt)}` : "Email not sent"}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div style={{ padding: "14px 24px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-start" }}>
              <button type="button" onClick={() => setShowDeleteConfirm(true)} style={{ ...textBtnStyle, color: T.danger, display: "flex", alignItems: "center", gap: 6 }}>
                <FaTrash size={12} /> Delete Candidate
              </button>
            </div>
          </>
        )}
      </div>

      {stageAction && (
        <StageActionModal candidateId={candidateId} {...stageAction} onClose={() => setStageAction(null)} onDone={refresh} />
      )}
      {logResultTarget && (
        <LogResultModal candidateId={candidateId} {...logResultTarget} onClose={() => setLogResultTarget(null)} onDone={refresh} />
      )}
      {rescheduleTarget && (
        <RescheduleEntryModal candidateId={candidateId} {...rescheduleTarget} onClose={() => setRescheduleTarget(null)} onDone={refresh} />
      )}
      {scorecardTarget && (
        <ScorecardModal entry={scorecardTarget} onClose={() => setScorecardTarget(null)} />
      )}
      {screeningModal && (
        <ScreeningModal candidateId={candidateId} {...screeningModal} onClose={() => setScreeningModal(null)} onDone={refresh} />
      )}
      {resultModalTarget && (
        <ResultModal candidateId={candidateId} {...resultModalTarget} onClose={() => setResultModalTarget(null)} onDone={refresh} />
      )}
      {emailTarget && candidate && (
        <SendEmailModal candidateId={candidateId} candidate={candidate} entry={emailTarget} onClose={() => setEmailTarget(null)} onDone={refresh} />
      )}
      {showResumeViewer && candidate && (
        <ResumeViewerModal candidateId={candidateId} fileName={candidate.resumeOriginalFileName} onClose={() => setShowResumeViewer(false)} />
      )}
      {showEditModal && candidate && (
        <EditCandidateModal candidate={candidate} onClose={() => setShowEditModal(false)} onSaved={refresh} />
      )}
      {showDeleteConfirm && candidate && (
        <ConfirmModal
          title="Delete this candidate?"
          message={`This will permanently delete ${candidate.name} and their resume. This action cannot be undone.`}
          confirmLabel="Delete"
          loading={deleting}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </div>
  );
}

function ProfileField({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.ink3, textTransform: "uppercase", letterSpacing: ".4px" }}>{label}</div>
      <div style={{ fontSize: 13.5, color: T.ink1, marginTop: 2 }}>{value || "—"}</div>
    </div>
  );
}

function ActionButton({ icon: Icon, color, label, onClick }) {
  return (
    <button type="button" onClick={onClick} className="plc-btn" style={{
      display: "flex", alignItems: "center", gap: 7, padding: "8px 14px", borderRadius: 8,
      border: `1px solid ${color}`, background: "transparent", color, fontSize: 12.5, fontWeight: 600,
      cursor: "pointer", fontFamily: T.fontBody,
    }}>
      <Icon size={11} /> {label}
    </button>
  );
}

/* ── Main page ────────────────────────────────────────────────────────── */
export default function PlacementAdminPage() {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState("ALL");
  const [resultFilter, setResultFilter] = useState("ALL");
  const [stageOutcomeFilter, setStageOutcomeFilter] = useState("ALL"); // Screening's Shortlisted/Not Shortlisted, or Test/Interview's Passed/Failed/On Hold — distinct from the final `result`
  // "Result" filter only makes sense against candidate.result outside these
  // stages; Enquiry/Screening/Test/Interview instead filter by that stage's
  // own recorded outcome, since a candidate isn't given a final result yet.
  const isStageOutcomeMode = stageFilter === "ENQUIRY" || stageFilter === "SCREENING" || stageFilter === "TEST" || stageFilter === "INTERVIEW";
  const [search, setSearch] = useState("");
  const [positionFilter, setPositionFilter] = useState("ALL");
  const [educationFilter, setEducationFilter] = useState("ALL");
  const [showAddModal, setShowAddModal] = useState(false);
  const [managingMaster, setManagingMaster] = useState(null); // "education" | "position" | null
  const [selectedId, setSelectedId] = useState(null);
  const [editCandidate, setEditCandidate] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resumeViewer, setResumeViewer] = useState(null); // { candidateId, fileName }
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);
  const [sortConfig, setSortConfig] = useState(null); // { key, direction: 'asc' | 'desc' }
  const [page, setPage] = useState(0); // 0-based, matches Spring Data Pageable
  const [pageSize, setPageSize] = useState(50);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [positionOptions, setPositionOptions] = useState([]);
  const [educationOptions, setEducationOptions] = useState([]);

  // Powers the Position/Education filter dropdowns below — reloaded after
  // the manage-master modal closes too, so adding/renaming/deleting an
  // entry there is reflected in the filter immediately.
  const loadFilterMasters = useCallback(() => {
    api.getPositions().then((list) => setPositionOptions(list || [])).catch(() => {});
    api.getEducations().then((list) => setEducationOptions(list || [])).catch(() => {});
  }, []);
  useEffect(() => { loadFilterMasters(); }, [loadFilterMasters]);

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list({
        stage: stageFilter !== "ALL" ? stageFilter : undefined,
        result: !isStageOutcomeMode && resultFilter !== "ALL" ? resultFilter : undefined,
        stageOutcome: isStageOutcomeMode && stageOutcomeFilter !== "ALL" ? stageOutcomeFilter : undefined,
        search: search || undefined,
        positionApplied: positionFilter !== "ALL" ? positionFilter : undefined,
        qualification: educationFilter !== "ALL" ? educationFilter : undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        page,
        size: pageSize,
      });
      setCandidates(data?.content || []);
      setTotalElements(data?.totalElements ?? 0);
      setTotalPages(data?.totalPages ?? 0);
    } catch (e) {
      toast.error(e.message || "Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, [stageFilter, resultFilter, stageOutcomeFilter, isStageOutcomeMode, search, positionFilter, educationFilter, dateFrom, dateTo, page, pageSize]);

  useEffect(() => {
    const t = setTimeout(loadCandidates, 250); // debounce search
    return () => clearTimeout(t);
  }, [loadCandidates]);

  // Switching stages invalidates whichever outcome selection belonged to the
  // previous mode (e.g. "Rejected" from Result mode isn't meaningful once
  // Screening's Shortlisted/Not Shortlisted mode is active).
  useEffect(() => {
    setResultFilter("ALL");
    setStageOutcomeFilter("ALL");
  }, [stageFilter]);

  // Any filter/page-size change invalidates the current page — jump back to
  // page 1 rather than risk landing past the end of a now-smaller result set.
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageFilter, resultFilter, stageOutcomeFilter, search, positionFilter, educationFilter, dateFrom, dateTo, pageSize]);

  const clearDateFilter = () => { setDateFrom(""); setDateTo(""); };
  const hasDateFilter = Boolean(dateFrom || dateTo);

  const sortedCandidates = useMemo(() => {
    if (!sortConfig) return candidates;
    const { key, direction } = sortConfig;
    return [...candidates].sort((a, b) => {
      const va = getSortValue(a, key);
      const vb = getSortValue(b, key);
      if (va < vb) return direction === "asc" ? -1 : 1;
      if (va > vb) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [candidates, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev?.key === key) return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      return { key, direction: "asc" };
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await api.exportExcel({
        search: search || undefined,
        stage: stageFilter !== "ALL" ? stageFilter : undefined,
        result: !isStageOutcomeMode && resultFilter !== "ALL" ? resultFilter : undefined,
        stageOutcome: isStageOutcomeMode && stageOutcomeFilter !== "ALL" ? stageOutcomeFilter : undefined,
        positionApplied: positionFilter !== "ALL" ? positionFilter : undefined,
        qualification: educationFilter !== "ALL" ? educationFilter : undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
      });
      toast.success("Downloaded successfully");
    } catch (e) {
      toast.error(e.message || "Failed to download report");
    } finally {
      setExporting(false);
    }
  };

  const handleViewResume = (e, candidate) => {
    e.stopPropagation();
    setResumeViewer({ candidateId: candidate.id, fileName: candidate.resumeOriginalFileName });
  };

  const handleConfirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeleting(true);
    try {
      await api.remove(deleteCandidate.id);
      toast.success("Candidate deleted");
      setDeleteCandidate(null);
      loadCandidates();
    } catch (err) {
      toast.error(err.message || "Failed to delete candidate");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ flex: 1, minHeight: 0, background: T.bg, fontFamily: T.fontBody, overflowY: "auto" }} className="plc-scroll">
      <GlobalStyles />

      <div style={{ padding: "26px 34px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 22, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: T.primary, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FaUserTie size={15} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 22, color: T.ink1, fontWeight: 700, fontFamily: T.fontDisplay }}>Recruitment</h2>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 2 }}>Track candidates from enquiry to final result</div>
            </div>
          </div>

          <div style={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 220, maxWidth: 420 }}>
            <div style={{ position: "relative", width: "100%" }}>
              <FaSearch size={12} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.ink3 }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, mobile, location…"
                style={{ ...inputStyle, width: "100%", paddingLeft: 32 }}
              />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={() => setManagingMaster("education")}
              style={{
                display: "flex", alignItems: "center", gap: 7, padding: "10px 14px",
                borderRadius: 9, border: `1px solid ${T.border}`, background: T.white, color: T.ink1,
                fontWeight: 600, fontSize: 13, fontFamily: T.fontBody, cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = MASTER_CONFIG.education.accent)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = T.border)}
            >
              <FaGraduationCap size={13} color={MASTER_CONFIG.education.accent} /> Education
            </button>
            <button
              type="button"
              onClick={() => setManagingMaster("position")}
              style={{
                display: "flex", alignItems: "center", gap: 7, padding: "10px 14px",
                borderRadius: 9, border: `1px solid ${T.border}`, background: T.white, color: T.ink1,
                fontWeight: 600, fontSize: 13, fontFamily: T.fontBody, cursor: "pointer",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = MASTER_CONFIG.position.accent)}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = T.border)}
            >
              <FaBriefcase size={13} color={MASTER_CONFIG.position.accent} /> Position
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={exporting}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: "10px 18px",
                borderRadius: 9, border: `1px solid ${T.border}`, background: T.white, color: T.ink1,
                fontWeight: 600, fontSize: 13.5, fontFamily: T.fontBody,
                cursor: exporting ? "not-allowed" : "pointer", opacity: exporting ? 0.6 : 1,
              }}
              onMouseEnter={(e) => { if (!exporting) e.currentTarget.style.borderColor = T.primary; }}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = T.border)}
            >
              <FaFileExcel size={13} color={T.green} /> {exporting ? "Exporting…" : "Export Excel"}
            </button>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 18px", borderRadius: 9, border: "none", background: T.primary, color: "#fff", fontWeight: 600, fontSize: 13.5, cursor: "pointer" }}
            >
              <FaPlus size={12} /> Add Candidate
            </button>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ width: 170 }}>
              <PillSelect value={stageFilter} options={STAGE_FILTER_OPTIONS} onChange={setStageFilter} placeholder="All Stages" />
            </div>
            <div style={{ width: 220 }}>
              {isStageOutcomeMode ? (
                <PillSelect
                  value={stageOutcomeFilter}
                  options={[{ value: "ALL", label: "All Outcomes" }, ...(stageFilter === "ENQUIRY" ? ENQUIRY_OUTCOME_OPTIONS : stageFilter === "SCREENING" ? SCREENING_OUTCOME_OPTIONS : OUTCOME_OPTIONS)]}
                  onChange={setStageOutcomeFilter}
                  placeholder="All Outcomes"
                />
              ) : (
                <PillSelect value={resultFilter} options={[{ value: "ALL", label: "All Results" }, ...RESULT_OPTIONS]} onChange={setResultFilter} placeholder="All Results" />
              )}
            </div>
            <div style={{ width: 190 }}>
              <PillSelect
                value={positionFilter}
                options={[{ value: "ALL", label: "All Positions" }, ...positionOptions.map((p) => ({ value: p.name, label: p.name }))]}
                onChange={setPositionFilter}
                placeholder="All Positions"
              />
            </div>
            <div style={{ width: 190 }}>
              <PillSelect
                value={educationFilter}
                options={[{ value: "ALL", label: "All Education" }, ...educationOptions.map((e) => ({ value: e.name, label: e.name }))]}
                onChange={setEducationFilter}
                placeholder="All Education"
              />
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: T.ink3, fontWeight: 600 }}>From</span>
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                style={dateInputStyle}
              />
              <span style={{ fontSize: 12, color: T.ink3, fontWeight: 600 }}>To</span>
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                style={dateInputStyle}
              />
              {hasDateFilter && (
                <button type="button" onClick={clearDateFilter} style={{ ...textBtnStyle, padding: "6px 4px", fontSize: 12.5 }}>
                  Clear
                </button>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: T.ink3, fontWeight: 600, whiteSpace: "nowrap" }}>Rows</span>
              <div style={{ width: 92 }}>
                <PillSelect
                  value={String(pageSize)}
                  options={PAGE_SIZE_OPTIONS}
                  onChange={(v) => setPageSize(Number(v))}
                  placeholder="Rows"
                />
              </div>
            </div>
          </div>
        </div>

        {loading && candidates.length === 0 ? (
          // Only the true initial load shows the full-page spinner — a
          // background refresh (e.g. after sending an email from the open
          // drawer) must NOT unmount the table, or the scrollable container
          // gets torn down and remounted at scroll-top, silently losing the
          // admin's scroll position by the time they close the drawer.
          <Spinner />
        ) : candidates.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: T.ink3, marginTop: 56, textAlign: "center" }}>
            <FaInbox size={26} style={{ color: T.borderStrong }} />
            <div style={{ fontSize: 14, color: T.ink2, fontWeight: 600, fontFamily: T.fontDisplay }}>No candidates here</div>
            <div style={{ fontSize: 13, maxWidth: 280 }}>
              {stageFilter === "ALL" && resultFilter === "ALL" && stageOutcomeFilter === "ALL" && positionFilter === "ALL" && educationFilter === "ALL" && !search && !hasDateFilter ? "No candidates have been added yet." : "Try a different filter or search."}
            </div>
          </div>
        ) : (
          <div style={{ background: T.white, borderRadius: T.radius, boxShadow: T.shadow, border: `1px solid ${T.border}`, overflow: "hidden" }}>
            <div className="plc-scroll" style={{ overflow: "auto", maxHeight: "calc(100vh - 320px)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#fafbfc", borderBottom: `1px solid ${T.border}` }}>
                    {SORTABLE_COLUMNS.map((col) => {
                      const active = sortConfig?.key === col.sortKey;
                      return (
                        <th
                          key={col.label}
                          title={col.tooltip}
                          style={{ ...theadCellStyle, cursor: col.sortKey ? "pointer" : "default", userSelect: "none" }}
                          onClick={col.sortKey ? () => handleSort(col.sortKey) : undefined}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            {col.label}
                            {col.sortKey && (
                              active ? (
                                sortConfig.direction === "asc"
                                  ? <FaSortUp size={11} color={T.primary} />
                                  : <FaSortDown size={11} color={T.primary} />
                              ) : (
                                <FaSort size={10} style={{ opacity: 0.35 }} />
                              )
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sortedCandidates.map((c, idx) => (
                    <tr
                      key={c.id}
                      className="plc-row"
                      onClick={() => setSelectedId(c.id)}
                      style={{ borderBottom: idx === sortedCandidates.length - 1 ? "none" : `1px solid ${T.border}`, cursor: "pointer" }}
                    >
                      <td style={{ ...tdCellStyle, color: T.ink3, fontFamily: T.fontMono, fontSize: 12 }}>{page * pageSize + idx + 1}</td>
                      <td style={{ ...tdCellStyle, fontWeight: 600, color: T.ink1 }}>{c.name}</td>
                      <td style={tdCellStyle}>{c.email}</td>
                      <td style={tdCellStyle}>{c.mobile}</td>
                      <td style={tdCellStyle}>{c.positionApplied || "—"}</td>
                      <td style={tdCellStyle}>{c.qualification || "—"}</td>
                      <td style={tdCellStyle}>{c.location || "—"}</td>
                      <td style={tdCellStyle}><StagePill stage={c.currentStage} /></td>
                      <td style={tdCellStyle}>
                        {c.result && c.result !== "PENDING" ? (
                          <ResultPill result={c.result} />
                        ) : (() => {
                          const outcome = getCurrentStageOutcome(c);
                          return outcome ? <StageOutcomePill stage={c.currentStage} outcome={outcome} /> : <ResultPill result={c.result} />;
                        })()}
                      </td>
                      <td style={{ ...tdCellStyle, fontFamily: T.fontMono, fontSize: 12 }}>{daysSince(c.updatedAt || c.createdAt)}</td>
                      <td style={{ ...tdCellStyle, fontFamily: T.fontMono, fontSize: 12 }}>{formatDate(c.createdAt)}</td>
                      <td style={tdCellStyle}>
                        {c.hasResume ? (
                          <button type="button" onClick={(e) => handleViewResume(e, c)} title="View resume" style={{
                            border: "none", background: "transparent", color: T.danger, cursor: "pointer", fontSize: 15, padding: 4,
                          }}>
                            <FaFilePdf />
                          </button>
                        ) : (
                          <span style={{ color: T.ink3, fontSize: 12.5 }}>—</span>
                        )}
                      </td>
                      <td style={tdCellStyle}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedId(c.id); }} title="View details" style={{
                            border: "none", background: "transparent", color: T.primary, cursor: "pointer", fontSize: 14.5, padding: 4,
                          }}>
                            <FaEye />
                          </button>
                          <button type="button" onClick={(e) => { e.stopPropagation(); setEditCandidate(c); }} title="Edit candidate" style={{
                            border: "none", background: "transparent", color: T.ink2, cursor: "pointer", fontSize: 13.5, padding: 4,
                          }}>
                            <FaPen />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setDeleteCandidate(c); }}
                            title="Delete candidate"
                            style={{ border: "none", background: "transparent", color: T.danger, cursor: "pointer", fontSize: 14, padding: 4 }}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderTop: `1px solid ${T.border}`, flexWrap: "wrap", gap: 10 }}>
              <div style={{ fontSize: 12.5, color: T.ink3 }}>
                Showing {candidates.length ? page * pageSize + 1 : 0}–{page * pageSize + candidates.length} of {totalElements}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  style={{ ...textBtnStyle, padding: "6px 10px", opacity: page === 0 ? 0.4 : 1, cursor: page === 0 ? "not-allowed" : "pointer" }}
                >
                  Previous
                </button>
                <span style={{ fontSize: 12.5, color: T.ink2, fontWeight: 600 }}>
                  Page {totalPages === 0 ? 0 : page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  style={{ ...textBtnStyle, padding: "6px 10px", opacity: page >= totalPages - 1 ? 0.4 : 1, cursor: page >= totalPages - 1 ? "not-allowed" : "pointer" }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showAddModal && (
        <AddCandidateModal onClose={() => setShowAddModal(false)} onCreated={loadCandidates} />
      )}
      {managingMaster && (
        <MasterListModal master={managingMaster} onClose={() => setManagingMaster(null)} onChanged={loadFilterMasters} />
      )}
      {selectedId && (
        <DetailDrawer candidateId={selectedId} onClose={() => setSelectedId(null)} onChanged={loadCandidates} />
      )}
      {editCandidate && (
        <EditCandidateModal
          candidate={editCandidate}
          onClose={() => setEditCandidate(null)}
          onSaved={loadCandidates}
        />
      )}
      {deleteCandidate && (
        <ConfirmModal
          title="Delete this candidate?"
          message={`This will permanently delete ${deleteCandidate.name} and their resume. This action cannot be undone.`}
          confirmLabel="Delete"
          loading={deleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteCandidate(null)}
        />
      )}
      {resumeViewer && (
        <ResumeViewerModal candidateId={resumeViewer.candidateId} fileName={resumeViewer.fileName} onClose={() => setResumeViewer(null)} />
      )}
    </div>
  );
}

const theadCellStyle = {
  textAlign: "left", padding: "11px 16px", fontSize: 11.5, fontWeight: 700, color: T.ink3,
  textTransform: "uppercase", letterSpacing: ".4px", fontFamily: T.fontDisplay,
  position: "sticky", top: 0, background: "#fafbfc", zIndex: 1,
};
const tdCellStyle = {
  padding: "13px 16px", fontSize: 13.5, color: T.ink2, verticalAlign: "middle",
};
const iconBtnStyle = {
  border: "none", background: "transparent", color: T.ink2, cursor: "pointer",
  fontSize: 14, padding: 8, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center",
};
const dateInputStyle = {
  ...inputStyle, width: 148, padding: "7px 10px", fontSize: 13, cursor: "pointer",
};